import { useCallback, useEffect, useRef, useState } from 'react';

import { ETATS_EN_COURS, ETATS_OUVERTS, lotImportApi } from '../lib/lotImportApi';

export const SCRUTATION_IMPORT_MS = 3000;

/**
 * Le travail d'import de la liste des camions d'un lot (lot 057).
 *
 * - lit le travail ouvert du lot au chargement (un travail terminé depuis une
 *   autre séance n'est pas rouvert) ;
 * - le relit tant que le serveur travaille (analyse, enregistrement) ;
 * - appelle `onTermine` quand l'enregistrement vient de se terminer, pour que
 *   l'écran recharge le lot.
 *
 * `actif` à faux (pas de droit d'écriture) : aucune lecture.
 */
export function useImportListeLot(lotId, { actif = true, onTermine } = {}) {
  const [job, setJob] = useState(null);
  const precedent = useRef(null);
  const surTermine = useRef(onTermine);
  surTermine.current = onTermine;

  const recharger = useCallback(async () => {
    if (!lotId) return;
    try {
      const courant = await lotImportApi.current(lotId);
      setJob(courant && ETATS_OUVERTS.includes(courant.status) ? courant : null);
    } catch (erreur) {
      // Une lecture manquée est sans conséquence : le bouton d'import reste disponible.
    }
  }, [lotId]);

  useEffect(() => {
    precedent.current = null;
    setJob(null);
    if (actif) recharger();
  }, [actif, recharger]);

  const jobId = job?.id;
  const statut = job?.status;
  useEffect(() => {
    if (!jobId || !ETATS_EN_COURS.includes(statut)) return undefined;
    const minuteur = setInterval(async () => {
      try {
        setJob(await lotImportApi.get(jobId));
      } catch (erreur) {
        // Une lecture manquée est sans conséquence : la suivante reprend.
      }
    }, SCRUTATION_IMPORT_MS);
    return () => clearInterval(minuteur);
  }, [jobId, statut]);

  useEffect(() => {
    if (statut === 'done' && ETATS_EN_COURS.includes(precedent.current)) surTermine.current?.();
    precedent.current = statut;
  }, [statut]);

  return { job, setJob, recharger };
}

export default useImportListeLot;
